import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiSearch, FiAlertCircle, FiZap, FiTarget, FiInfo } = FiIcons;

const RootCauseAnalysis = ({ analyses = [] }) => {
  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
      <div className="p-6 border-b border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/50">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-rose-100 dark:bg-rose-900/30 rounded-lg">
            <SafeIcon icon={FiSearch} className="text-rose-600 dark:text-rose-400 w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Root Cause Analysis</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">Identifiserte årsakssammenhenger bak risikoindikatorer</p>
          </div>
        </div>
      </div>

      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        {analyses.map((analysis, idx) => (
          <motion.div
            key={analysis.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: idx * 0.1 }}
            className="p-5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiAlertCircle} className="text-rose-500 w-4 h-4" />
                <h4 className="font-bold text-slate-900 dark:text-white uppercase tracking-wider text-xs">
                  Analyse: {analysis.risk_indicator}
                </h4>
              </div>
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                analysis.impact_level === 'high' ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/30' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30'
              }`}>
                {analysis.impact_level} Impact
              </span>
            </div>

            {/* Primary Drivers */}
            <div className="mb-6">
              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-3 flex items-center gap-1">
                <SafeIcon icon={FiZap} className="w-3 h-3" /> Hoveddrivere
              </p>
              <div className="space-y-3">
                {analysis.primary_drivers.map((driver, dIdx) => (
                  <div key={dIdx}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-slate-700 dark:text-slate-300">{driver.name}</span>
                      <span className="font-bold text-slate-900 dark:text-white">{driver.weight}%</span>
                    </div>
                    <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-1.5 overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${driver.weight}%` }}
                        className="bg-indigo-500 h-full rounded-full"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Contributing Factors */}
            <div className="mb-6">
              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2 flex items-center gap-1">
                <SafeIcon icon={FiInfo} className="w-3 h-3" /> Medvirkende faktorer
              </p>
              <div className="flex flex-wrap gap-2">
                {analysis.contributing_factors.map((factor, fIdx) => (
                  <span key={fIdx} className="px-2 py-1 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-[10px] rounded border border-slate-200 dark:border-slate-700">
                    {factor}
                  </span>
                ))}
              </div>
            </div>

            {/* Suggested Focus */}
            <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-100 dark:border-indigo-900/30">
              <p className="text-xs font-bold text-indigo-700 dark:text-indigo-400 mb-2 flex items-center gap-1">
                <SafeIcon icon={FiTarget} className="w-3 h-3" /> Anbefalt intervensjonsfokus
              </p>
              <ul className="space-y-1.5">
                {analysis.suggested_focus.map((item, sIdx) => (
                  <li key={sIdx} className="text-[11px] text-slate-700 dark:text-slate-300 flex items-start gap-1.5">
                    <span className="mt-1 w-1 h-1 rounded-full bg-indigo-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default RootCauseAnalysis;